import React, { useState, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { ClusterOutlined } from '@ant-design/icons';
import ParentOrgIndicator from './components/ParentOrgIndicator';

import {
  Col,
  Row,
  Tabs,
  Skeleton,
  Table,
  Badge,
  Button,
  Empty,
  Popover,
  Tag,
  Drawer,
  Card,
  Spin,
  message,
} from 'antd';
import { TeamOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons';

//locals from shared
import { PageTabContentLayout, PageHeaderLayout } from '../../shared/layout';
//locals
import OrganizationDetails from './components/OrganizationDetails';
import HeaderBar from './components/HeaderBar';
import SearchBar from './components/SearchBar';
import UsersLoader from '../../../core/components/orgs/users/UsersLoader';
import DraggableOrgTree from './components/DraggableOrgTree';
import ResizeObserverErrorBoundary from './components/ResizeObserverErrorBoundary';
import { permissionsFromRoles } from '../../../core/utils/dataManipulating/organization';
//utils
import {
  buildOrganizationTree,
  buildAntTree,
} from '../../../core/utils/dataManipulating/organization';
import _, { isEmpty } from 'lodash';
import './styles/index.css';
import SendEmailConfigView from './SendEmailConfigView';
import {
  // mediajelOrgIdProduction,
  mediajelOrgIdStaging,
} from '../../../core/utils/constants/constants';
import SEARCH_ORGS from '../../../core/GraphQl/Queries/SEARCH_ORGS';

const style = {
  sideBar: {
    width: '100%',
    backgroundColor: '#fff',
    border: '0.8px solid #e8e8e8',
    marginTop: '42px',
  },
  header: {
    padding: '10px 0px 0px 30px',
  },
  container: {
    border: '0.5px solid #e8e8e8',
    padding: '0.5px',
  },
  customerName: {
    marginLeft: 4,
  },
};

const useMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const OrganizationsView = ({
  onOrgSelect,
  createOrg,
  deleteOrg,
  disableOrg,
  enableOrg,
  updateOrg,
  moveOrg,
  onChange,
  onSwitchChange,
  currentRole,
  clickCreateNewOrg,
  organizations,
  currentOrg,
  selectedOrg,
  loadingOrgDetail,
  currentuser,
  match,
  assumeRole,
  updateOrgSendEmailConfig,
  refetchOrganizations,
  createDspAdvertiser,
}) => {
  let showShareReport = false;

  if (selectedOrg && selectedOrg.parentOrg) {
    // note: before merging to production, replace mediajelOrgIdStaging to mediajelOrgIdProduction
    showShareReport =
      selectedOrg === mediajelOrgIdStaging ||
      selectedOrg.parentOrg
        .map((org) => org.id === mediajelOrgIdStaging)
        .some((val) => val === true) ||
      selectedOrg.id === mediajelOrgIdStaging;
  }

  let [activeTab, setActiveTab] = useState('1');
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const isMobile = useMobile();

  const [openOrgSendEmail, setOpenOrgSendEmail] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const parentOrgId = currentuser?.role?.org?.id;
  const [searchOrgs] = useLazyQuery(SEARCH_ORGS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data && data.orgs) {
        setDisplayedOrganization(data.orgs);
        setSearchLoading(false);
      }
    },
    onError: () => {
      setSearchLoading(false);
    },
  });

  // const showModal = () => {
  //   setOpenOrgSendEmail(true);
  // };

  const handleOk = () => {
    setOpenOrgSendEmail(false);
  };

  const handleCancel = () => {
    setOpenOrgSendEmail(false);
  };

  const { isAdmin } = currentuser;
  // check selectedOrg value if it is already saved by checking if ID exist
  const isOrgAlreadySaved = (selectedOrg) => {
    return _.get(selectedOrg, 'id', false);
  };

  const preview = () => {
    assumeRole(selectedOrg);
  };
  const [displayedOrganization, setDisplayedOrganization] = useState(organizations);
  const columns = [
    {
      title: 'Users',
      dataIndex: 'name',
      render: (text, record, i) => {
        //Filter out the parent organizations thats only available for the login org/user
        const showableParent = record.parentOrg.filter((parent) => {
          return organizations.find((org) => org.name === parent.name);
        });
        const popupMessage = () => {
          return (
            <Row>
              {showableParent && showableParent.length > 0
                ? showableParent.map((org) => {
                    return (
                      <Col xs={24} key={org.id}>
                        {org.name}
                      </Col>
                    );
                  })
                : '--'}
            </Row>
          );
        };
        return (
          <>
            <Row justify="center" align="middle">
              <Col xs={22}>
                <Badge
                  dot
                  style={
                    Object.keys(
                      permissionsFromRoles([record.roles.find((role) => role.isMaster)])
                    ).includes('ENABLED')
                      ? { backgroundColor: '#52c41a' }
                      : null
                  }
                />
                <span style={style.customerName}> {` ${record.name} `}</span>
                {record && selectedOrg && record.id === selectedOrg.id ? (
                  <Tag color="#87d068">Selected</Tag>
                ) : null}
              </Col>
              <Col xs={2}>
                {isSearching && showableParent && showableParent.length > 0 ? (
                  <Popover title="Parent Org/s" content={popupMessage()}>
                    <ClusterOutlined />
                  </Popover>
                ) : null}
              </Col>
            </Row>
          </>
        );
      },
      sorter: (a, b) => a.name < b.name,
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },
  ];

  // Consolidated search handler - handles both onSearch and onSearchChange
  const handleSearchChange = useCallback(
    (value) => {
      if (value && value.trim()) {
        setIsSearching(true);
        setSearchLoading(true);
        searchOrgs({
          variables: {
            parentid: parentOrgId,
            searchTerm: value.trim(),
            first: 100,
          },
        });
      } else {
        setIsSearching(false);
        setSearchLoading(false);
        setDisplayedOrganization(organizations);
      }
    },
    [parentOrgId, searchOrgs, organizations]
  );

  // Handle org moving via drag and drop
  const handleOrgMove = async (orgId, targetParentId) => {
    try {
      
      const orgToMove = organizations.find(org => org.id === orgId);
      const targetParent = organizations.find(org => org.id === targetParentId);
      
      if (!orgToMove) {
        message.error('Organization to move not found');
        return { status: 'failure', error: 'Organization to move not found' };
      }
      
      if (!targetParent) {
        message.error('Target parent organization not found');
        return { status: 'failure', error: 'Target parent organization not found' };
      }

      // Show loading message
      message.loading('Moving organization...', 0);
      
      try {
        // Build disconnect array (current parent orgs) - array of objects with id
        const disconnect = orgToMove.parentOrg.map(parent => ({ id: parent.id }));
        
        // Build connect array (new parent hierarchy - target parent's parents + target parent) - array of objects with id
        const connect = [...targetParent.parentOrg.map(parent => ({ id: parent.id })), { id: targetParent.id }];
      
        
        // Call the MOVE_ORG mutation with proper structure
        const response = await moveOrg({
          variables: {
            id: orgId,
            connect: connect,
            disconnect: disconnect
          }
        });

        message.destroy();
       
        
        // Check if the response indicates success
        if (response && response.status === 'success') {
          message.success(`Successfully moved "${orgToMove.name}" under "${targetParent.name}"`);
          // Refresh the organizations list
          await refetchOrganizations();
          return { status: 'success', error: null, data: response.data };
        } else {
          // Even if there was an error, the move might have actually succeeded on the backend
          // Check if we have response data that indicates a successful move
          if (response && response.data) {
            message.success(`Successfully moved "${orgToMove.name}" under "${targetParent.name}"`);
            // Refresh the organizations list
            await refetchOrganizations();
            return { status: 'success', error: null, data: response.data };
          } else {
            message.error('Failed to move organization');
            return { status: 'failure', error: response?.error || 'No data returned from moveOrg mutation' };
          }
        }
        
      } catch (error) {
        message.destroy();
        message.error(`Failed to move organization: ${error.message || error}`);
        console.error('Move org error:', error);
        return { status: 'failure', error: error.message || error };
      }
    } catch (error) {
      message.destroy();
      message.error(`Failed to move organization: ${error.message || error}`);
      return { status: 'failure', error: error.message || error };
    }
  };
  return (
    <React.Fragment>
      {showShareReport && selectedOrg && selectedOrg.id && (
        <SendEmailConfigView
          selectedOrg={selectedOrg}
          currentOrg={currentOrg}
          open={openOrgSendEmail}
          setOpenOrgSendEmail={setOpenOrgSendEmail}
          handleOk={handleOk}
          handleCancel={handleCancel}
          updateOrgSendEmailConfig={updateOrgSendEmailConfig}
        />
      )}
      <PageHeaderLayout
        data={{}}
        titleIcon={
          <TeamOutlined
            style={{
              marginRight: '10px',
              marginLeft: '10px',
              fontSize: 20,
            }}
          />
        }
        rightContent={
          <Row gutter={8} align="middle">
            {isMobile && (
              <>
                <Col>
                  <Button icon={<MenuOutlined />} onClick={() => setMobileSidebarVisible(true)} />
                </Col>
                {activeTab === '1' && (
                  <Col>
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<PlusOutlined />}
                      onClick={clickCreateNewOrg}
                      style={{ marginLeft: 8 }}
                    />
                  </Col>
                )}
              </>
            )}
          </Row>
        }
      />
      <PageTabContentLayout>
        <Row gutter={8}>
          {!isMobile ? (
            <Col xs={0} sm={0} md={24} lg={8} xl={8} xxl={8} style={{ marginBottom: '10px' }}>
              <div style={style.sideBar}>
                {isAdmin && isOrgAlreadySaved(selectedOrg) && activeTab !== '2' && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={clickCreateNewOrg}
                    style={{
                      marginBottom: 16,
                      width: '100%',
                      padding: '8px 16px',
                      height: '40px',
                    }}
                    icon={<PlusOutlined />}
                  >
                    Create Org
                  </Button>
                )}
                <SearchBar
                  onSearch={handleSearchChange}
                  onSearchChange={handleSearchChange}
                  searchLoading={searchLoading}
                />
                <ResizeObserverErrorBoundary>
                  <DraggableOrgTree
                    organizations={displayedOrganization}
                    currentOrg={currentOrg}
                    selectedOrg={selectedOrg}
                    onOrgSelect={onOrgSelect}
                    onOrgMove={handleOrgMove}
                    buildOrganizationTree={buildOrganizationTree}
                    buildAntTree={buildAntTree}
                    isSearching={isSearching}
                  />
                </ResizeObserverErrorBoundary>
              </div>
            </Col>
          ) : (
            <Drawer
              title="Organizations"
              placement="left"
              closable={true}
              onClose={() => setMobileSidebarVisible(false)}
              visible={mobileSidebarVisible}
              width="80%"
            >
              <div style={style.sideBar}>
                <SearchBar
                  onSearch={handleSearchChange}
                  onSearchChange={handleSearchChange}
                  searchLoading={searchLoading}
                />
                <Table
                  style={{ position: 'relative' }}
                  showHeader={false}
                  scroll={{ y: 800, x: true }}
                  columns={columns}
                  bordered={false}
                  dataSource={buildAntTree(
                    buildOrganizationTree(displayedOrganization, currentOrg ? currentOrg.name : ''),
                    ''
                  )}
                  rowClassName={(record, index) => {
                    if (record && selectedOrg) {
                      if (record.id === selectedOrg.id) return 'tableSelectedRow';
                    }
                  }}
                  pagination={false}
                  onRow={(record, rowIndex) => {
                    return {
                      onClick: (e) => {
                        onOrgSelect(record);
                      },
                    };
                  }}
                  rowKey={(record) => `${record.id}`}
                />
              </div>
            </Drawer>
          )}
          <Col xs={24} sm={24} md={24} lg={16} xl={16} xxl={16}>
            <Tabs
              defaultActiveKey={match.params.user && match.params.org ? '2' : '1'}
              tabBarStyle={{ margin: '0 10px' }}
              onChange={(key) => setActiveTab(key.toString())}
            >
              <Tabs.TabPane tab="Org Details" key="1" disabled={selectedOrg === null}>
                <React.Fragment>
                  {loadingOrgDetail ? (
                    <Card>
                      <Spin spinning={true}>
                        <Skeleton avatar paragraph={{ rows: 10 }} />
                      </Spin>
                    </Card>
                  ) : selectedOrg ? (
                    <OrganizationDetails
                      createOrg={createOrg}
                      deleteOrg={deleteOrg}
                      disableOrg={disableOrg}
                      enableOrg={enableOrg}
                      updateOrg={updateOrg}
                      onChange={onChange}
                      onSwitchChange={onSwitchChange}
                      selectedOrg={selectedOrg}
                      currentRole={currentRole}
                      currentOrg={currentOrg}
                      clickCreateNewOrg={clickCreateNewOrg}
                      organizations={organizations}
                      preview={preview}
                      currentuser={currentuser}
                      setDisplayedOrganization={setDisplayedOrganization}
                      refetchOrganizations={refetchOrganizations}
                      createDspAdvertiser={createDspAdvertiser}
                    />
                  ) : (
                    <Skeleton avatar paragraph={{ rows: 10 }} />
                  )}
                </React.Fragment>
              </Tabs.TabPane>
              <Tabs.TabPane
                tab="Org Users"
                key="2"
                disabled={selectedOrg ? isEmpty(selectedOrg.id) : true}
              >
                <Card
                  headStyle={{
                    border: '0.5px solid #e8e8e8',
                    background: '#FAFAFA',
                  }}
                  title="Users"
                >
                  {selectedOrg && (
                    <ParentOrgIndicator
                      selectedOrg={selectedOrg}
                      currentOrg={currentOrg}
                      isCreating={!selectedOrg.id}
                      currentUserOrgId={currentuser?.role?.org?.id}
                    />
                  )}
                  <Spin spinning={false}>
                    {selectedOrg ? (
                      selectedOrg.id ? (
                        <UsersLoader
                          organizations={organizations}
                          currentRole={currentRole}
                          currentuser={currentuser}
                          selectedOrg={selectedOrg}
                        />
                      ) : (
                        <Empty />
                      )
                    ) : (
                      <Empty />
                    )}
                  </Spin>
                </Card>
              </Tabs.TabPane>
            </Tabs>
          </Col>
        </Row>
      </PageTabContentLayout>
    </React.Fragment>
  );
};

export default OrganizationsView;
